import { browserAgent } from "@/shared/telemetry/newrelic";
import type { TrackEventParams, TrackingEvent, Tracker } from "./types";

declare global {
  interface Window {
    tracker?: Tracker;
  }
}

// Global tracker instance (created immediately, initialized later)

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate RFC3339 timestamp (ISO 8601 format)
 * Returns format: YYYY-MM-DDTHH:MM:SS.sssZ
 */
function generateTimestamp(): string {
  return new Date().toISOString();
}

interface TrackerError extends Error {
  response?: { status: number; statusText: string };
  endpoint?: string;
  eventType?: string;
}

export class TrackerImpl implements Tracker {
  private authToken: string | null = null;
  private eventBuffer: TrackEventParams[] = [];
  public isInitialized = false;
  private readonly ENDPOINT = "/api/v1/events";
  private readonly APPLICATION = "web-frontend";

  constructor() {
    // Instance is created immediately, will be initialized later
  }

  /**
   * Initialize the tracker with auth token
   */
  initialize(token: string): void {
    if (this.isInitialized) {
      console.warn(
        "[Tracker] Tracker already initialized. Skipping re-initialization.",
      );
      return;
    }

    this.authToken = token;
    this.isInitialized = true;

    // Process buffered events
    this.processBufferedEvents();
  }

  /**
   * Track an event - sends immediately if initialized, otherwise buffers
   */
  track(params: TrackEventParams): void {
    if (!this.isInitialized) {
      // Buffer the event for later processing
      this.eventBuffer.push(params);
      return;
    }

    // Send immediately - sendEvent handles all errors internally and never rejects
    this.sendEvent(params);
  }

  /**
   * Process all buffered events
   */
  private processBufferedEvents(): void {
    if (this.eventBuffer.length === 0) return;

    console.log(
      `[Tracker] Processing ${this.eventBuffer.length} buffered events from before initialization`,
    );

    // Send all buffered events - sendEvent handles all errors internally and never rejects
    for (const eventParams of this.eventBuffer) {
      this.sendEvent(eventParams);
    }

    // Clear the buffer
    this.eventBuffer = [];
  }

  /**
   * Send a single event to the backend
   */
  private async sendEvent(params: TrackEventParams): Promise<void> {
    if (!this.authToken) {
      console.error("[Tracker] Cannot send event: no auth token");
      return;
    }

    // Build the tracking event
    const event: TrackingEvent = {
      eventId: generateUUID(),
      requestId: generateUUID(),
      eventType: params.eventType,
      timestamp: generateTimestamp(),
      merchantId: params.merchantId,
      checkoutId: params.checkoutId,
      orderId: params.orderId,
      customerId: params.customerId,
      countryCode: params.countryCode,
      currencyCode: params.currencyCode,
      application: this.APPLICATION,
      eventData: params.eventData,
      spans: params.spans || [],
    };

    // Send as array (single event wrapped in array)
    const payload = [event];
    const payloadString = JSON.stringify(payload);

    try {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.authToken}`,
      };

      const response = await fetch(this.ENDPOINT, {
        method: "POST",
        headers,
        body: payloadString,
        keepalive: true,
      });

      if (!response.ok) {
        // Create a more descriptive error with context
        const status = response.status;
        const statusText = response.statusText;

        // Special handling for 401 (Unauthorized) errors
        if (status === 401) {
          const error: TrackerError = new Error(
            `Failed to send event: ${status} ${statusText} - Unauthorized (invalid or expired auth token)`,
          );
          // Add response status to error for easier identification
          error.response = { status, statusText };
          error.endpoint = this.ENDPOINT;
          error.eventType = params.eventType;
          throw error;
        }

        // For other errors, include status and context
        const error: TrackerError = new Error(
          `Failed to send event: ${status} ${statusText}`,
        );
        error.response = { status, statusText };
        error.endpoint = this.ENDPOINT;
        error.eventType = params.eventType;
        throw error;
      }

      console.log(`[Tracker] Successfully sent event: ${params.eventType}`);
    } catch (error) {
      // Log error with context
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      // Check if error has response property (from our TrackerError type)
      const hasResponse =
        error && typeof error === "object" && "response" in error;
      const responseStatus = hasResponse
        ? (error as TrackerError).response?.status
        : undefined;
      const is401 = errorMessage.includes("401") || responseStatus === 401;

      if (is401) {
        console.warn(
          `[Tracker] Failed to send event (401 Unauthorized): ${params.eventType}`,
          {
            endpoint: this.ENDPOINT,
            eventType: params.eventType,
            checkoutId: params.checkoutId,
            orderId: params.orderId,
            error: errorMessage,
          },
        );
      } else {
        console.error(
          `[Tracker] Failed to send event: ${params.eventType}`,
          error,
          {
            endpoint: this.ENDPOINT,
            eventType: params.eventType,
            checkoutId: params.checkoutId,
            orderId: params.orderId,
          },
        );
      }

      try {
        browserAgent.log("TRACKER_SEND_EVENT_FAILED", {
          customAttributes: {
            eventType: params.eventType,
            endpoint: this.ENDPOINT,
            checkoutId: params.checkoutId,
            orderId: params.orderId,
            error: errorMessage,
            status: responseStatus,
          },
        });
      } catch (error) {
        console.error("[Tracker] Failed to send event to New Relic:", error);
      }

      // Don't re-throw - the error is already logged and handled
      // Events are not retried - they are lost on failure
      // This prevents unhandled promise rejections
    }
  }
}

// Create tracker instance immediately (before initialization) and expose globally
const trackerInstance = new TrackerImpl();
window.tracker = trackerInstance;
